import _ from "lodash";
import React from "react";
import { useI18n } from "Hooks";

import classes from "./PollSettings.scss";
import {
  Button,
  ButtonDisclosure,
  DatePicker,
  Dropdown,
  DropdownMenu,
  Modal,
  ToggleSwitch,
} from "@toddle-edu/ds-web";
import moment from "moment";
import { useDispatch } from "Hooks/useDispatch";
import { updatePollSetting } from "MessagingV2/modules/MessagingModule";

const getSettingOptions = t => [
  {
    key: "expiresAt",
    label: t("common:closes_on"),
    type: "datePicker",
  },
  {
    key: "isResultPublic",
    label: t("common:show_result_to_everyone"),
    type: "switch",
  },
  {
    key: "isMultipleVoteAllowed",
    label: t("common:user_can_vote_multiple_options"),
    type: "switch",
  },

  {
    key: "isResponseEditAllowed",
    label: t("common:user_can_edit_response"),
    type: "switch",
  },
];

const SettingOption = ({ option, value, onChange }) => {
  const renderSwitch = () => (
    <ToggleSwitch
      dsVersion="2.0"
      checked={value}
      onChange={() => onChange(!value)}
    />
  );

  const renderDatePicker = () => {
    return (
      <div className={classes.datePickerContainer}>
        <DatePicker
          dsVersion="2.0"
          picker="date"
          value={value ? moment(value) : null}
          onChange={date => {
            onChange(date ? date.toDate() : null);
          }}
          minDate={moment()}
          showTimePicker
          variant="outlined"
        />
      </div>
    );
  };

  const renderSelect = () => {
    const selectionOption = _.find(option.options, { key: value });
    return (
      <Dropdown
        dsVersion="2.0"
        overlay={
          <DropdownMenu
            showSelection
            dsVersion="2.0"
            options={option.options}
            onClick={({ key }) => {
              onChange(key);
            }}
            selectionType="radio"
            value={value}
          />
        }
      >
        <ButtonDisclosure
          dsVersion="2.0"
          variant="neutral"
          type="inline"
          className={classes.selectButton}
        >
          {selectionOption ? selectionOption.label : ""}
        </ButtonDisclosure>
      </Dropdown>
    );
  };

  const renderOption = () => {
    switch (option.type) {
      case "switch":
        return renderSwitch();
      case "select":
        return renderSelect();
      case "datePicker":
        return renderDatePicker();
      default:
        return null;
    }
  };

  return (
    <div className={classes.settingOption}>
      <label className="text-body">{option.label}</label>
      {renderOption()}
    </div>
  );
};

export const PollSettingsModal = props => {
  const { onCloseModal, interaction, messageId } = props;
  const { t } = useI18n(["common"]);

  const dispatch = useDispatch();

  const [settings, setSettings] = React.useState(() => {
    return {
      expiresAt: _.get(interaction, "expiresAt", null),
      isResultPublic: _.get(interaction, "isResultPublic", true),
      isMultipleVoteAllowed: _.get(interaction, "isMultipleVoteAllowed", false),
      isResponseEditAllowed: _.get(interaction, "isResponseEditAllowed", true),
    };
  });

  const onUpdatePollSettings = () => {
    dispatch(
      updatePollSetting({
        messageId,
        settings,
        pollId: interaction.id,
      })
    );
    onCloseModal();
  };

  const onSettingChange = (key, value) => {
    setSettings({
      ...settings,
      [key]: value,
    });
  };

  const settingOptions = getSettingOptions(t);

  return (
    <Modal
      dsVersion="2.0"
      height="310px"
      hasOverlay
      width="480px"
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      onClose={onCloseModal}
      isOpen
    >
      <Modal.Header
        title={t("common:edit_with_label", {
          label: t("common:poll_settings"),
        })}
        isClosable
      />
      <Modal.Body>
        <div className={classes.pollSettingsContainer}>
          {_.map(settingOptions, option => {
            const onChange = value => {
              onSettingChange(option.key, value);
            };
            return (
              <SettingOption
                key={option.key}
                option={option}
                value={settings[option.key]}
                onChange={onChange}
              />
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={classes.footerContainer}>
          <Button
            dsVersion="2.0"
            variant="neutral"
            type="outlined"
            onClick={onCloseModal}
          >
            {t("common:cancel")}
          </Button>
          <Button
            dsVersion="2.0"
            variant="progressive"
            onClick={onUpdatePollSettings}
          >
            {t("common:confirm")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const PollSettings = ({ settings, setSettings }) => {
  const { t } = useI18n(["common"]);

  const onSettingChange = (key, value) => {
    setSettings({
      ...settings,
      [key]: value,
    });
  };

  const settingOptions = getSettingOptions(t);
  return (
    <div className={classes.pollSettingsContainer}>
      {_.map(settingOptions, option => {
        const onChange = value => {
          onSettingChange(option.key, value);
        };
        return (
          <SettingOption
            key={option.key}
            option={option}
            value={settings[option.key]}
            onChange={onChange}
          />
        );
      })}
    </div>
  );
};

export default PollSettings;
